/**
 * TrameDebriefPanel.jsx — Cockpit compact « débrief moniteur ».
 *
 * Rendu lorsque l'URL contient ?view=debrief et que le visiteur a
 * l'accès enseignant. Affiche pour chaque compétence de la phase courante :
 *   - Critère de réussite (mesurable)
 *   - Questions pédagogiques (à poser à l'élève)
 *   - Débrief élève (points à faire verbaliser)
 *
 * Compact, imprimable, utilisable sur téléphone en fin de séance.
 */
import React from 'react';
import {
  CheckCircle2, MessageCircle, Clipboard, ChevronRight, Presentation,
} from 'lucide-react';

const AMBER = '#f59e0b';

function CompetenceRow({ ex, color = AMBER }) {
  const hasCritere = !!ex.critere;
  const questions = ex.questions && ex.questions.length
    ? ex.questions
    : (ex.question ? [ex.question] : []);
  const debrief = ex.debrief || [];
  if (!hasCritere && !questions.length && !debrief.length) return null;

  return (
    <article
      className="rounded-xl border border-white/10 bg-[#141416] p-4 print:break-inside-avoid print:border-black/25"
      data-testid={`debrief-${ex.id}`}
    >
      <h4
        className="text-white font-black text-[14.5px] sm:text-[16px] leading-tight mb-3 print:text-black"
        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
      >
        {ex.title}
      </h4>

      <div className="space-y-2.5">
        {hasCritere && (
          <div
            className="rounded-lg px-3 py-2 border"
            style={{ borderColor: `${color}44`, background: `${color}0d` }}
          >
            <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest mb-1" style={{ color }}>
              <CheckCircle2 className="w-3 h-3" /> Critère de réussite
            </div>
            <p className="text-white/90 text-[12.5px] leading-relaxed print:text-black/85">{ex.critere}</p>
          </div>
        )}

        {questions.length > 0 && (
          <div className="rounded-lg px-3 py-2 border border-rose-500/30 bg-rose-950/20">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-rose-200 mb-1 print:text-rose-800">
              <MessageCircle className="w-3 h-3" /> Questions à poser
            </div>
            <ul className="space-y-0.5">
              {questions.map((q, i) => (
                <li key={i} className="text-rose-100 italic text-[12.5px] leading-relaxed print:text-rose-900 flex items-start gap-1.5">
                  <ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span>{q}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {debrief.length > 0 && (
          <div className="rounded-lg px-3 py-2 border border-white/10 bg-black/25">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-white/70 mb-1 print:text-black/70">
              <Clipboard className="w-3 h-3" /> Débrief élève
            </div>
            <ul className="space-y-0.5">
              {debrief.map((d, i) => (
                <li key={i} className="text-white/80 text-[12.5px] leading-relaxed print:text-black/80 flex items-start gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-white/60 print:bg-black/60 mt-1.5 flex-shrink-0" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </article>
  );
}

export default function TrameDebriefPanel({ phase }) {
  if (!phase) return null;
  const items = (phase.exercises || []).filter(ex =>
    ex.critere || (ex.questions && ex.questions.length) || ex.question || (ex.debrief && ex.debrief.length)
  );
  if (!items.length) return null;
  const color = phase.color || AMBER;

  return (
    <section
      className="mb-6 rounded-2xl border overflow-hidden print:break-inside-avoid"
      style={{
        background: `linear-gradient(135deg, ${AMBER}0f 0%, transparent 60%)`,
        borderColor: `${AMBER}55`,
      }}
      data-testid={`debrief-panel-${phase.id}`}
    >
      <div className="p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-1">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border"
            style={{ color: AMBER, borderColor: `${AMBER}55`, background: `${AMBER}15` }}
          >
            <Presentation className="w-3 h-3" /> Cockpit moniteur · Vue débrief
          </span>
        </div>
        <h3
          className="text-white font-black text-xl sm:text-2xl leading-tight mt-2 print:text-black"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Débrief {phase.code || phase.id} · {phase.title}
        </h3>
        <p className="text-white/60 text-[12.5px] mt-1 print:text-black/60">
          {items.length} compétence{items.length > 1 ? 's' : ''} à dérouler avec l'élève. Fais-lui verbaliser <strong className="text-white print:text-black">ce qu'il a réussi</strong>, ce qu'il a compris et ce qu'il retient.
        </p>

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {items.map(ex => <CompetenceRow key={ex.id} ex={ex} color={color} />)}
        </div>
      </div>
    </section>
  );
}
